import {useState} from "react";
import {Button} from "@reside/ui";
import {SftpConfig} from "../../services/HubAndSpokeService";
import {ButtonLink} from "../../atoms/button-link";
import {Column, ListTable} from "../../atoms/list-table";
import {Spinner} from "../../atoms/spinner";
import {useSftpConfigsQuery} from "./hooks/useSftpConfigsQuery";
import {useDeleteSftpConfig} from "./hooks/useDeleteSftpConfig";
import {HubAndSpokePage} from "./atoms/HubAndSpokePage";
import {ConfirmDeleteModal} from "./atoms/ConfirmDeleteModal";
import {ButtonsWrapper, TableContainer} from "./styles";

type Row = {
  rowData: SftpConfig;
};

const SFTP_CONFIGURATIONS_PATH = "/admin/hub-and-spoke/sftp-configurations";

export const SftpConfigurations = () => {
  const [configToDelete, setConfigToDelete] = useState<SftpConfig>();

  const {data, isLoading} = useSftpConfigsQuery();
  const {mutate: deleteSftpConfig, isLoading: isDeleting} =
    useDeleteSftpConfig();

  return (
    <HubAndSpokePage
      title="SFTP Configurations"
      actions={
        <ButtonLink
          to={`${SFTP_CONFIGURATIONS_PATH}/create`}
          color="primary"
          outline={false}
          testId="add-target-form-button"
        >
          New SFTP Configuration
        </ButtonLink>
      }
    >
      {isLoading || !data ? (
        <Spinner />
      ) : (
        <TableContainer>
          <ListTable
            key="sftp-configs"
            sortedData={data}
            getRowId={row => row.rowData.id}
            noRowsMessage="No SFTP configurations yet."
          >
            <Column
              cellRenderer={(row: Row) => <p>{row.rowData.name}</p>}
              label="Name"
              width={220}
            />
            <Column
              cellRenderer={(row: Row) => <p>{row.rowData.hostname}</p>}
              label="Hostname/IP Address"
              width={240}
            />
            <Column
              cellRenderer={(row: Row) => <p>{row.rowData.port}</p>}
              label="Port"
              width={80}
            />
            <Column
              cellRenderer={(row: Row) => <p>{row.rowData.notes}</p>}
              label="Notes"
            />
            <Column
              cellRenderer={({rowData}: Row) => (
                <ButtonsWrapper>
                  <ButtonLink
                    to={`${SFTP_CONFIGURATIONS_PATH}/${rowData.id}/edit`}
                    color="primary"
                    testId="edit-target-form-button"
                  >
                    Edit
                  </ButtonLink>
                  <Button
                    color="danger"
                    outline
                    onClick={() => setConfigToDelete(rowData)}
                  >
                    Delete
                  </Button>
                </ButtonsWrapper>
              )}
              width={200}
            />
          </ListTable>
        </TableContainer>
      )}
      <ConfirmDeleteModal
        title="Delete SFTP Configuration"
        isOpen={!!configToDelete}
        isDeleting={isDeleting}
        onClose={() => setConfigToDelete(undefined)}
        onConfirm={() =>
          deleteSftpConfig(configToDelete.id, {
            onSettled: () => setConfigToDelete(undefined),
          })
        }
      >
        Are you sure you want to delete the SFTP configuration{" "}
        <strong>{configToDelete?.name || configToDelete?.hostname}</strong>?
      </ConfirmDeleteModal>
    </HubAndSpokePage>
  );
};
